import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Seo } from "../components/Seo";
import { getPaymentStatus } from "../lib/api";
import { usePro } from "../context/ProContext";

export default function ProSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | paid | pending | failed
    const [plan, setPlan] = useState("pro");
    const [accessCode, setAccessCode] = useState(null);
    const [copied, setCopied] = useState(false);
    const tries = useRef(0);
    const { activate } = usePro();

    useEffect(() => {
        if (!sessionId) {
            setState("failed");
            return;
        }
        let timer;
        const poll = async () => {
            try {
                const data = await getPaymentStatus(sessionId);
                if (data.payment_status === "paid" && data.pro_token) {
                    activate(data.pro_token, data.plan, "owner");
                    setPlan(data.plan === "business" ? "business" : "pro");
                    setAccessCode(data.pro_token);
                    setState("paid");
                    return;
                }
                if (data.payment_status === "failed" || data.status === "expired") {
                    setState("failed");
                    return;
                }
            } catch {
                /* tenta de novo */
            }
            tries.current += 1;
            if (tries.current < 20) {
                timer = setTimeout(poll, 2000);
            } else {
                setState("pending");
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId, activate]);

    return (
        <main className="py-24 px-6 max-w-2xl mx-auto text-center min-h-[50vh]">
            <Seo title="Assinatura Pro — ScorpionPDF" description="Confirmação da assinatura do plano Pro." noindex />
            {state === "checking" && (
                <div data-testid="pro-success-loading">
                    <Loader2 className="w-12 h-12 animate-spin text-[#0284C7] mx-auto" aria-hidden="true" />
                    <h1 className="font-poster text-4xl text-black mt-4">CONFIRMANDO PAGAMENTO…</h1>
                    <p className="text-gray-600 mt-2 text-sm">Isso leva poucos segundos. Não feche esta página.</p>
                </div>
            )}
            {state === "paid" && (
                <div data-testid="pro-success-paid">
                    <CheckCircle2 className="w-14 h-14 text-[#0284C7] mx-auto" aria-hidden="true" />
                    <h1 className="font-poster text-5xl text-black mt-4">{plan === "business" ? "BUSINESS ATIVADO!" : "PRO ATIVADO!"}</h1>
                    <p className="text-gray-700 mt-3">
                        {plan === "business"
                            ? "Sua assinatura Business está ativa neste navegador: 250 MB por arquivo, 100 arquivos por operação e gestão de equipe para até 5 usuários."
                            : "Sua assinatura está ativa neste navegador: arquivos de até 100 MB, 50 arquivos por operação e 1.000 páginas por documento."}
                    </p>
                    {accessCode && (
                        <div className="mt-6 border-2 border-black bg-[#F0F9FF] p-4 text-left" data-testid="access-code-box">
                            <p className="font-bold text-sm text-black">Seu código de acesso</p>
                            <p className="text-xs text-gray-600 mt-1">
                                Guarde com cuidado: é ele que reativa seu plano em outro navegador (em Planos → “Tem um
                                código de acesso?”).
                            </p>
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <code className="flex-1 min-w-0 break-all border-2 border-black bg-white px-3 py-2 text-xs font-mono-tag" data-testid="access-code-value">
                                    {accessCode}
                                </code>
                                <button
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(accessCode);
                                            setCopied(true);
                                        } catch { setCopied(false); }
                                    }}
                                    data-testid="access-code-copy"
                                    className="px-4 py-2 bg-black text-[#22D3EE] font-mono-tag text-xs font-bold uppercase hover:bg-[#0284C7] hover:text-white transition-colors"
                                >
                                    {copied ? "Copiado!" : "Copiar"}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link
                            to="/ferramentas"
                            data-testid="pro-success-tools-link"
                            className="inline-block px-8 py-4 bg-[#0284C7] hover:bg-black text-white font-poster text-xl tracking-wide transition-colors"
                        >
                            USAR AS FERRAMENTAS
                        </Link>
                        {plan === "business" && (
                            <Link
                                to="/equipe"
                                data-testid="pro-success-team-link"
                                className="inline-block px-8 py-4 border-2 border-black bg-white hover:bg-black hover:text-white text-black font-poster text-xl tracking-wide transition-colors"
                            >
                                MONTAR MINHA EQUIPE
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {state === "pending" && (
                <div data-testid="pro-success-pending">
                    <Loader2 className="w-12 h-12 text-gray-400 mx-auto" aria-hidden="true" />
                    <h1 className="font-poster text-4xl text-black mt-4">PAGAMENTO EM ANÁLISE</h1>
                    <p className="text-gray-600 mt-2 text-sm">
                        O Stripe ainda está confirmando. Recarregue esta página em instantes.
                    </p>
                </div>
            )}
            {state === "failed" && (
                <div data-testid="pro-success-failed">
                    <XCircle className="w-14 h-14 text-red-700 mx-auto" aria-hidden="true" />
                    <h1 className="font-poster text-4xl text-black mt-4">NÃO CONFIRMADO</h1>
                    <p className="text-gray-600 mt-2 text-sm">
                        Não conseguimos confirmar o pagamento. Se o valor foi cobrado, fale com o suporte.
                    </p>
                    <Link
                        to="/planos"
                        data-testid="pro-success-retry-link"
                        className="inline-block mt-8 px-8 py-4 border-2 border-black bg-white hover:bg-black hover:text-white text-black font-poster text-xl transition-colors"
                    >
                        VOLTAR AOS PLANOS
                    </Link>
                </div>
            )}
        </main>
    );
}
